import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  metaSelector,
  connectivityListSelector,
  fetchDeviceList,
  setDeviceFilters,
} from 'store';
import { DeviceFilter, DeviceList } from 'models/connectivity';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { createDeviceFilterAttribute } from 'utils/filtering';
import DeviceTable from 'components/connectivity/DeviceTable';
import DeviceFilters from 'components/connectivity/DeviceFilters';
import Card from 'components/UI/Card';
import FoldableCard from 'components/UI/FoldableCard';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Pagination, { Page, PagePagination } from 'components/UI/Pagination';
import PageWrapper from 'components/UI/PageWrapper';
import SearchField from 'components/UI/SearchField';
import CardHeader from 'components/UI/Card/CardHeader';
import Loader from 'components/UI/Loader';
import Button from 'components/UI/Button';
import { fetchDeviceListFromStoredPage } from 'store/connectivityState/connectivityState';
import { showErrorToast } from 'utils/toast';

// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};
let hasRun = false;

const ConnectivityStartPage: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const meta = useAppSelector(metaSelector);
  const {
    data,
    isLoading,
    filters,
    totalCount,
    lastEvaluatedId,
    onlyFavorites,
    error,
  }: DeviceList = useAppSelector(connectivityListSelector);

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Fetch deviceDetails if id selected
  useEffect(() => {
     if (error) {
    showErrorToast(
        `${error.title}`
      );
    }
    if (!hasRun) {
      dispatch(fetchDeviceList([], initialPagination.offset, ''));
    }
    hasRun = true;
  
  }, [dispatch, error]);

  // Fetch devices
  const fetchDevices = useCallback(
    (deviceFilters: DeviceFilter[], offset?: number, startId?: string) => {
      dispatch(fetchDeviceList(deviceFilters, offset ?? 0, startId));
    },
    [dispatch]
  );

  // Fetch devices from previous page
  const fetchDevicesFromPage = useCallback(
    (deviceFilters: DeviceFilter[], pageToShow: Page) => {
      dispatch(
        fetchDeviceListFromStoredPage(
          deviceFilters,
          pageToShow.data,
          totalCount,
          pageToShow.lastId,
          onlyFavorites
        )
      );
    },
    [dispatch, totalCount, onlyFavorites]
  );

  // Search
  const onSearch = useCallback(
    (value: string) => {
      const searchFilters = createDeviceFilterAttribute(value);
      setPagination(initialPagination);
      if (searchFilters.length) {
        fetchDevices(searchFilters, initialPagination.offset);
      }
    },
    [fetchDevices]
  );

  // Clear search
  const onClear = useCallback(() => {
    setPagination(initialPagination);
    fetchDevices([], initialPagination.offset);
  }, [fetchDevices]);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      if (paginate.pageToShow) {
        fetchDevicesFromPage(filters.data ?? [], paginate.pageToShow);
      } else {
        fetchDevices(filters.data ?? [], paginate.offset, paginate.startId);
      }
    },
    [fetchDevices, fetchDevicesFromPage, filters.data]
  );

  // Toggle filters
  const toggleFilters = useCallback(() => {
    dispatch(setDeviceFilters(!filters.active));
  }, [dispatch, filters.active]);

  // Apply filters
  const onFilter = useCallback(
    (deviceFilters: DeviceFilter[]) => {
      setPagination(initialPagination);
      fetchDevices(deviceFilters, initialPagination.offset);
    },
    [fetchDevices]
  );

  // Reset pagination
  const resetPagination = useCallback(() => {
    setPagination(initialPagination);
    fetchDevices([], initialPagination.offset);
  }, [fetchDevices]);

  // Render results
  const results = useMemo(() => {
    let updatedPagination = pagination;
    if (isLoading) {
      return <Loader center />;
    }
    if (!data || data.length <= 0) {
      return null;
    }
    if (lastEvaluatedId) {
      const pages = pagination.pages || [];
      const currentPage = pages.find((page) => page.page === pagination.page);
      if (!currentPage) {
        pages.push({
          page: pagination.page,
          lastId: lastEvaluatedId || '',
          data,
        });
      }
      updatedPagination = {
        ...pagination,
        pages,
      };
    }
    return (
      <Card>
        <CardHeader
          title={
            onlyFavorites
              ? 'connectivity.favorites_table_title'
              : 'connectivity.table_title'
          }
        />
        <DeviceTable devices={data} />
        <Pagination
          noItems={data.length}
          totalCount={totalCount || 0}
          pagination={updatedPagination}
          setPagination={onSetPagination}
        />
      </Card>
    );
  }, [
    data,
    isLoading,
    pagination,
    totalCount,
    lastEvaluatedId,
    onlyFavorites,
    onSetPagination,
  ]);

  return (
    <PageWrapper>
      <Heading>
        <FormattedMessage id="connectivity.title" />
      </Heading>
      <Card>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="search.title" />
        </Heading>
        <SearchField
          onSearch={onSearch}
          onClear={onClear}
          placeholder={intl.formatMessage({
            id: 'connectivity.search_placeholder',
          })}
          buttons={
            <Button
              onClick={toggleFilters}
              backgroundColor="surface"
              color={filters.active ? 'primaryDark' : 'onSurface'}
              disabled={isLoading}
            >
              <FormattedMessage id="filters.btn" />
            </Button>
          }
          disable={isLoading}
        />
      </Card>
      <FoldableCard open={filters.active}>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="filters.title" />
        </Heading>
        <DeviceFilters
          onFilter={onFilter}
          onClear={resetPagination}
          data={meta.data}
        />
      </FoldableCard>
      {results}
    </PageWrapper>
  );
};

export default ConnectivityStartPage;
